import mixpanel from 'mixpanel-browser'

export type MixpanelVersionT = `v${number}@${number}-${number}-${number}` // v1.0@YYYY-MM-DD
export class MixpanelClient<EventSchemaT extends Record<string, any>> {
  private static _instance = mixpanel

  version?: {
    globalPropertiesVersion: MixpanelVersionT
    userPropertiesVersion: MixpanelVersionT
  }
  constructor(
    private readonly projectToken?: string,
    private config?: Parameters<typeof mixpanel.init>[1],
    version?: MixpanelClient<EventSchemaT>['version'],
  ) {
    this.version = version
  }

  get initialized() {
    return '__loaded' in MixpanelClient._instance && MixpanelClient._instance.__loaded
  }

  initialize(deviceType: string) {
    if (!this.projectToken) return
    MixpanelClient._instance.init(this.projectToken, this.config)
    MixpanelClient._instance.register({
      $device_type: deviceType,
      $user_agent: navigator.userAgent,
    })
  }

  static getMixpanel() {
    return MixpanelClient._instance
  }

  /**
   * @see https://github.com/mixpanel/docs/blob/main/reference/Ingestion%20API/events/track-event.md#limits
   * @description
   * Must be smaller than 1MB of uncompressed JSON.
   * Must have fewer than 255 properties.
   * All nested object properties must have fewer than 255 keys and max nesting depth is 3.
   * All array properties must have fewer than 255 elements.
   */
  track<K extends Extract<keyof EventSchemaT, string>>(
    eventName: Parameters<typeof MixpanelClient._instance.track>[0],
    properties?: EventSchemaT[K] extends never ? never : EventSchemaT[K],
    optionsOrCallback?: Parameters<typeof MixpanelClient._instance.track>[2],
    callback?: Parameters<typeof MixpanelClient._instance.track>[3],
  ) {
    if (!this.initialized) return
    // properties가 있는 경우 제한사항 검증
    if (properties) {
      const validation = MixpanelValidator.validateMixpanelLimits(properties)

      if (!validation.isValid) {
        if (this.config?.debug) {
          console.error('Mixpanel 제한사항 위반:', {
            eventName,
            errors: validation.errors,
          })
        }

        // 제한사항을 위반한 경우 이벤트 전송을 중단하거나 제한된 데이터만 전송
        return
      }
    }

    MixpanelClient._instance.track(eventName, properties, optionsOrCallback, callback)
  }

  identify(...args: Parameters<typeof MixpanelClient._instance.identify>) {
    if (!this.initialized) return
    MixpanelClient._instance.identify(...args)
  }

  setGlobalEventProperty(...args: Parameters<typeof MixpanelClient._instance.register>) {
    if (!this.initialized) return
    const props = {
      ...args[0],
      globalPropertiesVersion: this.version?.globalPropertiesVersion,
    }

    args[0] = props

    MixpanelClient._instance.register(...args)
  }

  setUserProperty(...args: Parameters<typeof MixpanelClient._instance.people.set>) {
    if (!this.initialized) return
    const props = {
      ...args[0],
      userPropertiesVersion: this.version?.userPropertiesVersion,
    }

    args[0] = props

    MixpanelClient._instance.people.set(...args)
  }

  unregister(...args: Parameters<typeof MixpanelClient._instance.unregister>) {
    if (!this.initialized) return
    MixpanelClient._instance.unregister(...args)
  }

  removeUserProperty(...args: Parameters<typeof MixpanelClient._instance.people.unset>) {
    if (!this.initialized) return
    MixpanelClient._instance.people.unset(...args)
  }

  getEventProperty(key: string) {
    if (!this.initialized) return
    return MixpanelClient._instance.get_property(key)
  }
}

// 검증 결과 타입
interface ValidationResult {
  isValid: boolean
  errors: string[]
}

// Mixpanel 제한사항 검증 유틸리티 클래스
class MixpanelValidator {
  // Mixpanel 제한사항 상수
  static MIXPANEL_LIMITS = {
    MAX_JSON_SIZE_BYTES: 1024 * 1024, // 1MB
    MAX_PROPERTIES: 255,
    MAX_NESTING_DEPTH: 3,
    MAX_ARRAY_ELEMENTS: 255,
    MAX_OBJECT_KEYS: 255,
  } as const
  /**
   * JSON 크기 검증 (1MB 미만)
   */
  static validateJsonSize(data: any): ValidationResult {
    try {
      const jsonString = JSON.stringify(data)
      const sizeInBytes = new Blob([jsonString]).size

      if (sizeInBytes >= MixpanelValidator.MIXPANEL_LIMITS.MAX_JSON_SIZE_BYTES) {
        return {
          isValid: false,
          errors: [
            `JSON 크기가 제한을 초과했습니다. 현재: ${sizeInBytes} bytes, 제한: ${MixpanelValidator.MIXPANEL_LIMITS.MAX_JSON_SIZE_BYTES} bytes`,
          ],
        }
      }

      return { isValid: true, errors: [] }
    } catch (error) {
      return {
        isValid: false,
        errors: [`JSON 직렬화 중 오류 발생: ${error}`],
      }
    }
  }

  /**
   * 객체의 프로퍼티 개수 검증 (255개 미만)
   */
  static validatePropertyCount(obj: Record<string, any>): ValidationResult {
    const propertyCount = Object.keys(obj).length

    if (propertyCount >= MixpanelValidator.MIXPANEL_LIMITS.MAX_PROPERTIES) {
      return {
        isValid: false,
        errors: [
          `프로퍼티 개수가 제한을 초과했습니다. 현재: ${propertyCount}, 제한: ${MixpanelValidator.MIXPANEL_LIMITS.MAX_PROPERTIES}`,
        ],
      }
    }

    return { isValid: true, errors: [] }
  }

  /**
   * 중첩 깊이 및 배열 요소 개수 검증
   */
  static validateNestingAndArrays(obj: any, currentDepth = 0): ValidationResult {
    const errors: string[] = []

    // 중첩 깊이 검증
    if (currentDepth > MixpanelValidator.MIXPANEL_LIMITS.MAX_NESTING_DEPTH) {
      errors.push(
        `중첩 깊이가 제한을 초과했습니다. 현재: ${currentDepth}, 제한: ${MixpanelValidator.MIXPANEL_LIMITS.MAX_NESTING_DEPTH}`,
      )
    }

    if (obj === null || typeof obj !== 'object') {
      return { isValid: errors.length === 0, errors }
    }

    // 배열 요소 개수 검증
    if (Array.isArray(obj)) {
      if (obj.length >= MixpanelValidator.MIXPANEL_LIMITS.MAX_ARRAY_ELEMENTS) {
        errors.push(
          `배열 요소 개수가 제한을 초과했습니다. 현재: ${obj.length}, 제한: ${MixpanelValidator.MIXPANEL_LIMITS.MAX_ARRAY_ELEMENTS}`,
        )
      }

      // 배열의 각 요소에 대해 재귀 검증
      for (const item of obj) {
        const result = this.validateNestingAndArrays(item, currentDepth + 1)
        errors.push(...result.errors)
      }
    } else {
      // 객체 키 개수 검증
      const keyCount = Object.keys(obj).length
      if (keyCount >= MixpanelValidator.MIXPANEL_LIMITS.MAX_OBJECT_KEYS) {
        errors.push(
          `객체 키 개수가 제한을 초과했습니다. 현재: ${keyCount}, 제한: ${MixpanelValidator.MIXPANEL_LIMITS.MAX_OBJECT_KEYS}`,
        )
      }

      // 객체의 각 값에 대해 재귀 검증
      for (const value of Object.values(obj)) {
        const result = this.validateNestingAndArrays(value, currentDepth + 1)
        errors.push(...result.errors)
      }
    }

    return { isValid: errors.length === 0, errors }
  }

  /**
   * 모든 Mixpanel 제한사항을 한번에 검증
   */
  static validateMixpanelLimits(data: any): ValidationResult {
    const allErrors: string[] = []

    // JSON 크기 검증
    const sizeResult = this.validateJsonSize(data)
    allErrors.push(...sizeResult.errors)

    // 프로퍼티 개수 검증 (최상위 객체인 경우)
    if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
      const propertyResult = this.validatePropertyCount(data)
      allErrors.push(...propertyResult.errors)
    }

    // 중첩 및 배열 검증
    const nestingResult = this.validateNestingAndArrays(data)
    allErrors.push(...nestingResult.errors)

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
    }
  }
}
